import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import Button from '../components/Button';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '40vh',
    minHeight: '300px',
    color: 'white',
    textAlign: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
    padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
}));

const FeatureCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-5px)',
    },
}));

const lmsStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Thrana Learning Management System',
    url: 'https://www.thranatech.com/products/learning-management-system',
    description:
        'Comprehensive learning management system for pharmacovigilance training, course management, and skill development.',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web-based',
    publisher: {
        '@type': 'Organization',
        name: 'Thranatech.com',
        url: 'https://www.thranatech.com',
    },
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
    },
};

const LearningManagementSystem = () => {
    const [currentBanner, setCurrentBanner] = useState(0);

    const banners = [
        '/productsSafety.jpg',
        '/banner1.jpg',
        '/banner2.jpg',
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentBanner((prev) => (prev + 1) % banners.length);
        }, 5000);

        return () => clearInterval(timer);
    }, []);

    const features = [
        {
            title: 'Course Management',
            description: 'Comprehensive course creation, organization, and delivery platform for training programs.',
            icon: <SchoolIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
        },
        {
            title: 'Assessment Tools',
            description: 'Built-in assessment and evaluation tools to track learner progress and performance.',
            icon: <AssessmentIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
        },
        {
            title: 'User Management',
            description: 'Robust user management system for learners, instructors, and administrators.',
            icon: <PeopleIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
        },
        {
            title: 'Progress Tracking',
            description: 'Advanced tracking and reporting features to monitor learning outcomes and completion.',
            icon: <TrackChangesIcon sx={{ fontSize: 60, color: '#3c005a' }} />,
        },
    ];

    return (
        <>
            <SEO
                title="Learning Management System | Thrana Training Platform"
                description="Thrana Learning Management System - Comprehensive platform for pharmacovigilance training, course management, and professional development."
                path="/products/learning-management-system"
                structuredData={lmsStructuredData}
            />
            <Box>
                <HeroSection>
                    {banners.map((banner, index) => (
                        <BannerImage
                            key={index}
                            sx={{
                                backgroundImage: `url(${banner})`,
                                opacity: currentBanner === index ? 1 : 0,
                            }}
                        />
                    ))}
                    <Overlay>
                        <Container maxWidth="md">
                            <Typography
                                variant="h3"
                                component="h1"
                                gutterBottom
                                sx={{
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                    fontWeight: 'bold',
                                    color: 'white',
                                }}
                            >
                                Learning Management System
                            </Typography>
                            <Typography
                                variant="h6"
                                paragraph
                                sx={{
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                                    color: 'white',
                                }}
                            >
                                Comprehensive Training and Development Platform
                            </Typography>
                        </Container>
                    </Overlay>
                </HeroSection>

                <Box sx={{ py: 8 }}>
                    <Container>
                        <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ color: '#3c005a' }}>
                            Key Features
                        </Typography>
                        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 4 }}>
                            Powerful learning management capabilities for effective training delivery
                        </Typography>

                        <Grid container spacing={4} sx={{ mt: 4 }}>
                            {features.map((feature, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <FeatureCard elevation={3}>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            {feature.icon}
                                            <Typography variant="h5" component="h3" sx={{ mt: 2, mb: 1 }}>
                                                {feature.title}
                                            </Typography>
                                            <Typography color="text.secondary">
                                                {feature.description}
                                            </Typography>
                                        </CardContent>
                                    </FeatureCard>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ mt: 8, textAlign: 'center' }}>
                            <Button component={Link} to="/contact" size="large">
                                Request a Demo
                            </Button>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </>
    );
};

export default LearningManagementSystem;

